import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, LogOut, Save, Users, Ticket } from "lucide-react";

function StatCard({ label, value }) {
  return (
    <div className="rounded-xl border-2 border-[#14283A]/10 bg-white p-5">
      <div className="text-sm text-[#14283A]/60">{label}</div>
      <div className="font-display text-3xl text-[#14283A]">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [signups, setSignups] = useState([]);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
  }, [ready, user, navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [c, w, s] = await Promise.all([
        api.get("/admin/site-config"),
        api.get("/admin/waitlist"),
        api.get("/admin/stats"),
      ]);
      setConfig(c.data);
      setSignups(w.data.signups);
      setStats(s.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (ready && user) loadAll();
  }, [ready, user, loadAll]);

  const set = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        phase: config.phase,
        prix_affiche: config.prix_affiche,
        prix_note: config.prix_note,
        booking_url: config.booking_url,
        places_total: parseInt(config.places_total, 10),
      };
      const { data } = await api.put("/admin/site-config", payload);
      setConfig(data);
      toast.success("Configuration enregistrée.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!ready || !user || !config) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F6F1E8]">
        <Loader2 className="h-8 w-8 animate-spin text-[#EA7A4F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F1E8]">
      <header className="border-b-2 border-[#14283A]/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="grad-bar flex h-9 w-9 items-center justify-center rounded-lg text-white">
              <Ticket className="h-5 w-5" />
            </span>
            <span className="font-display text-lg text-[#14283A]">SZN TRIP · ADMIN</span>
          </div>
          <button
            onClick={() => { logout(); navigate("/admin/login"); }}
            className="flex items-center gap-2 rounded-full border-2 border-[#14283A] px-4 py-2 text-sm font-bold text-[#14283A] transition-colors hover:bg-[#14283A] hover:text-white"
            data-testid="admin-logout"
          >
            <LogOut className="h-4 w-4" /> Déconnexion
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Inscrits" value={stats?.total_inscrits ?? 0} />
          <StatCard label="En team" value={stats?.inscrits_team ?? 0} />
          <StatCard label="Solo" value={stats?.inscrits_solo ?? 0} />
          <StatCard label="Places restantes" value={stats?.places_restantes ?? "—"} />
        </div>

        <Tabs defaultValue="config">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="config" data-testid="tab-config">Configuration</TabsTrigger>
            <TabsTrigger value="waitlist" data-testid="tab-waitlist">
              Liste prioritaire ({signups.length})
            </TabsTrigger>
          </TabsList>

          {/* Config */}
          <TabsContent value="config">
            <div className="max-w-2xl rounded-2xl border-2 border-[#14283A]/10 bg-white p-6 sm:p-8" data-testid="config-panel">
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <Label>Phase du site</Label>
                  <Select value={config.phase} onValueChange={(v) => set("phase", v)}>
                    <SelectTrigger data-testid="config-phase"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="waitlist">Waitlist (liste prioritaire, aucun paiement)</SelectItem>
                      <SelectItem value="booking">Booking (réservation, lien agence)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#14283A]/50">
                    Pilote le bloc prix et le CTA sur toute la page.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="prix">Prix affiché</Label>
                  <Input id="prix" value={config.prix_affiche} onChange={(e) => set("prix_affiche", e.target.value)} data-testid="config-prix" />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="note">Note prix (waitlist)</Label>
                  <Input id="note" value={config.prix_note} onChange={(e) => set("prix_note", e.target.value)} data-testid="config-note" />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="url">Lien de réservation agence (phase booking)</Label>
                  <Input id="url" placeholder="https://…" value={config.booking_url} onChange={(e) => set("booking_url", e.target.value)} data-testid="config-url" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="total">Places totales</Label>
                    <Input id="total" type="number" value={config.places_total} onChange={(e) => set("places_total", e.target.value)} data-testid="config-total" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Places restantes (auto)</Label>
                    <div className="flex h-10 items-center rounded-md border-2 border-dashed border-[#14283A]/20 bg-[#F6F1E8] px-3 text-sm font-semibold text-[#14283A]" data-testid="config-restantes-auto">
                      {stats?.places_restantes ?? "—"} · calculé sur {stats?.total_inscrits ?? 0} inscrit(s)
                    </div>
                  </div>
                </div>

                <button
                  onClick={save}
                  disabled={saving}
                  className="grad-bar flex items-center justify-center gap-2 rounded-full px-6 py-3 font-bold text-white transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                  data-testid="config-save"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Enregistrer
                </button>
              </div>
            </div>
          </TabsContent>

          {/* Waitlist */}
          <TabsContent value="waitlist">
            <div className="rounded-2xl border-2 border-[#14283A]/10 bg-white p-2 sm:p-4" data-testid="waitlist-panel">
              {signups.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-16 text-[#14283A]/50">
                  <Users className="h-10 w-10" />
                  <p>Aucune inscription pour l'instant.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Prénom</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Tél.</TableHead>
                        <TableHead>Âge</TableHead>
                        <TableHead>Région</TableHead>
                        <TableHead>Station/activité</TableHead>
                        <TableHead>Solo/Team</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {signups.map((s) => (
                        <TableRow key={s.id} data-testid="waitlist-row">
                          <TableCell className="font-semibold">{s.prenom}</TableCell>
                          <TableCell>{s.email}</TableCell>
                          <TableCell>{s.telephone}</TableCell>
                          <TableCell>{s.tranche_age}</TableCell>
                          <TableCell>{s.region}</TableCell>
                          <TableCell>{s.station_activite || "—"}</TableCell>
                          <TableCell>
                            {s.solo_ou_team === "team" ? `Team (${s.taille_team || "?"})` : "Solo"}
                          </TableCell>
                          <TableCell>{new Date(s.date_inscription).toLocaleDateString("fr-FR")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
